import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { MenuItem, Paper, Select } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Services
import { exportSightings, getLatestSightings } from '../../../services/sighting.service';

// Redux
import { fetchAllSettings } from '../../../redux/slices/settingsSlice';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { useExport } from '../../../hooks/useExport';

// Utils
import { makeSightingListColumn } from '../../../utils/columns/makeColumn';
import { Storage, getRetainState } from '../../../utils/Storage';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';

// Types
import { SearchBarFilterTypes } from '../../../constants/Constants';
import { FilterableTypes } from '../../../types/FilterableTypes';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import SearchBar from '../../common/SearchBar/SearchBar';

interface DevicesProp {
  accessKey?: string;
}

export default function Sightings(props: DevicesProp): JSX.Element {
  const dispatch = useAppDispatch();
  const settingStates = useAppSelector((state) => state.settings);
  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [
      {
        lastSeenDateTime: '-1',
      },
    ],
    entityType: 'SightingLatest',
    filters: [],
  });
  const [equipmentStatus, setEquipmentStatus] = useState<number | undefined>();
  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);

  const { isExporting, startExport } = useExport('SightingLatest');

  const columns = useMemo(() => makeSightingListColumn(), []);

  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getLatestSightings,
    exportAsync: exportSightings,
    params: paramFilter,
    serverSide: true,
  });

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.SIGHTINGS);

    getRetainState(SearchBarFilterTypes.SIGHTINGS, 'equipmentStatus', setEquipmentStatus);
    getRetainState(SearchBarFilterTypes.SIGHTINGS, 'lastSeenDateTime', setLastSeenDateTimeFrom, true, FilterableTypes.GreaterThanOrEqual);
    getRetainState(SearchBarFilterTypes.SIGHTINGS, 'lastSeenDateTime', setLastSeenDateTimeTo, true, FilterableTypes.LessThanOrEqual);

    if (filterState) {
      setParamFilter({
        entityType: 'SightingLatest',

        filters: filterState.filters,
      });
    }
  };

  useEffect(() => {
    dispatch(fetchAllSettings());
    intialDefaultFilters();
  }, []);

  const onSearchChange = (filters: any[]) => {
    const finalFilters = [...filters];
    if (equipmentStatus !== undefined) {
      finalFilters.push({
        equipmentStatus: equipmentStatus,
        filterType: FilterableTypes.Equals,
      });
    }

    if (lastSeenDateTimeFrom && lastSeenDateTimeTo) {
      finalFilters.push({
        lastSeenDateTime: FormatForApi(moment(lastSeenDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          lastSeenDateTime: FormatForApi(moment(lastSeenDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }

    setParamFilter({ entityType: 'SightingLatest', filters: finalFilters });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.SIGHTINGS, { isMoreFilter: true, filters: finalFilters });
  };

  return (
    <React.Fragment>
      <SearchBar
        pageKey={SearchBarFilterTypes.SIGHTINGS}
        onSearch={onSearchChange}
        onReset={() => {
          setEquipmentStatus(undefined);
          setLastSeenDateTimeFrom(null);
          setLastSeenDateTimeTo(null);
          setParamFilter({ entityType: 'SightingLatest', filters: [] });
        }}
        allowSearchAll={true}
        columns={columns}
        components={
          <div style={{ display: 'flex', marginBottom: 5, flexWrap: 'wrap' }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <FormInputCollection label='Status'>
                <Select
                  value={equipmentStatus ?? 'all'}
                  variant='outlined'
                  id='equipmentStatus'
                  name='equipmentStatus'
                  size='small'
                  style={{ width: '220px' }}
                  onChange={(e: any) => {
                    setEquipmentStatus(e.target.value === 'all' ? undefined : e.target.value);
                  }}
                >
                  <MenuItem value={'all'}>All</MenuItem>
                  {settingStates.equipmentStatus?.value.map((val, index: number) => {
                    return (
                      <MenuItem key={index} value={val.text}>
                        {val.value}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormInputCollection>
              <FormInputCollection label='Last Seen Date' style={{ marginLeft: '24px' }}>
                <div style={{ marginRight: '8px' }}>
                  <DatePicker
                    value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                    label='From'
                    format={shortDateFormat}
                    slotProps={{ textField: { size: 'small' } }}
                    sx={{ width: '185px' }}
                    onChange={(date) => {
                      setLastSeenDateTimeFrom(date ? date.toDate() : null);
                    }}
                  />
                </div>
                <DatePicker
                  value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                  label='To'
                  format={shortDateFormat}
                  slotProps={{ textField: { size: 'small' } }}
                  sx={{ width: '185px' }}
                  onChange={(date) => {
                    setLastSeenDateTimeTo(date ? date.toDate() : null);
                  }}
                />
              </FormInputCollection>
            </LocalizationProvider>
          </div>
        }
      />
      <Paper>
        <Table
          {...table}
          isExporting={isExporting}
          canExportData={() => true}
          exportData={() => startExport(paramFilter.filters ?? [])}
          showSearchBar={false}
          showColumnToggler={false}
          showAction={true}
          initialState={{
            sorting: [
              {
                id: 'lastSeenDateTime',
                desc: true,
              },
            ],
          }}
          getCoreRowModel={getCoreRowModel()}
        />
      </Paper>
    </React.Fragment>
  );
}
